import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ESegmentedToggleOption } from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HD_BODYGRAPH_GATES } from '@/app/human-design/chart/bodygraph/hd-bodygraph-metadata';
import { HD_GATE_INFO_PROVIDER } from '@/app/human-design/chart/bodygraph/gate-info/hd-gate-info-provider';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '@/app/human-design/chart/bodygraph/hd-planet-info.service';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import {
  buildHdGateLineViewModel,
  HdGateLineViewModel,
} from '@/app/human-design/chart/bodygraph/gate-info/line/hd-gate-line.view-model';

/** Lay-out van de tweede vergelijk-sectie: onder elkaar of naast elkaar. */
export type HdCompareLayout = 'stacked' | 'side';

/**
 * IChing Cheat Sheet pagina: een rij kleine poortnummer-knoppen (1..64). Klik op
 * een poort → de 6 lijnen van die poort als kaarten in een 2×3 grid (lijn 1·2·3
 * bovenste rij, 4·5·6 onderste rij). Content-taal-toggle (NL/EN) analoog aan de
 * {@link HdPlanetCheatSheetPageComponent}, los van de globale app-taal.
 *
 * De kaart view-models worden gebouwd via de gedeelde `buildHdGateLineViewModel`
 * (zelfde bron als de full-variant lijnen).
 */
@Component({
  selector: 'hd-iching-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-iching-cheat-sheet-page.component.html',
  styleUrl: './hd-iching-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdIchingCheatSheetPageComponent {
  private readonly provider = inject(HD_GATE_INFO_PROVIDER);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly gates = HD_BODYGRAPH_GATES;
  protected readonly selectedGate = signal<number>(1);

  /** Tweede (vergelijk-)sectie: standaard niet geladen, los geselecteerde poort. */
  protected readonly showSecondSection = signal<boolean>(false);
  protected readonly selectedGate2 = signal<number>(2);
  /** Hoe de tweede sectie getoond wordt: onder elkaar (default) of naast elkaar. */
  protected readonly sectionLayout = signal<HdCompareLayout>('stacked');
  /** Icoonknoppen van de indeling-toggle (gedeelde e-segmented-toggle). */
  protected readonly sectionLayoutOptions: readonly ESegmentedToggleOption<HdCompareLayout>[] = [
    { value: 'stacked', label: 'Onder elkaar', icon: 'table-rows' },
    { value: 'side', label: 'Naast elkaar', icon: 'table-columns' },
  ];

  /** Compacte (kleinere) kaarten i.p.v. de standaard hoogte. Default uit. */
  protected readonly compactCards = signal<boolean>(false);

  constructor() {
    // Engels is de default content-taal op deze cheat-sheet-pagina.
    this.contentLang.setOverride('en');
  }

  /** De 6 lijn-kaart view-models van de geselecteerde poort (volgt de content-taal). */
  protected readonly lineCards = computed<readonly HdGateLineViewModel[]>(() =>
    this.buildLineCards(this.selectedGate()),
  );

  /** Idem voor de tweede vergelijk-sectie. */
  protected readonly lineCards2 = computed<readonly HdGateLineViewModel[]>(() =>
    this.buildLineCards(this.selectedGate2()),
  );

  /** Bouwt de 6 lijn-kaarten voor een poort in de actieve content-taal. */
  private buildLineCards(gate: number): readonly HdGateLineViewModel[] {
    const lang = this.contentLang.effectiveLang();
    const planetTranslations = this.planetInfoService.planetTranslationsAllIn(lang);
    const iconLabels = this.gateInfoService.iconLabelsIn(lang);
    const gateInfo = this.provider.gateInfoIn(gate, lang);
    const voice = gateInfo.translations.voice;
    const fearDetail = this.gateInfoService.fearIconDetailIn(gate, lang);
    return gateInfo.translations.lines.map((line, index) =>
      buildHdGateLineViewModel({ gate, lineNumber: index + 1, line, planetTranslations, iconLabels, voice, fearDetail }),
    );
  }

  protected selectGate(gate: number): void {
    this.selectedGate.set(gate);
  }

  protected selectGate2(gate: number): void {
    this.selectedGate2.set(gate);
  }

  protected toggleSecondSection(): void {
    this.showSecondSection.update(value => !value);
  }

  protected setSectionLayout(layout: HdCompareLayout): void {
    this.sectionLayout.set(layout);
  }

  protected toggleCompactCards(): void {
    this.compactCards.update(value => !value);
  }
}
